import type { Rng } from './rng.js';

/**
 * Стычка решается автоматически по раундам, а игрок читает лог.
 * Отдельное тактическое поле — самый дорогой и самый рискованный кусок
 * работы, и он этой игре не нужен: драка во дворе должна занимать
 * десять секунд чтения, а не пять минут возни.
 */

export interface Fighter {
  name: string;
  sila: number;
  chotkost: number;
  health: number;
  /** Оружие и кастеты: плоская прибавка к урону. */
  weapon?: number;
}

export interface FightRound {
  round: number;
  text: string;
}

export interface FightResult {
  won: boolean;
  rounds: FightRound[];
  /** Сколько здоровья потерял игрок. */
  damageTaken: number;
}

const MAX_ROUNDS = 8;

/** Сила стороны: кулаки, оружие и немного чоткости за счёт наглости. */
export function sideStrength(side: readonly Fighter[]): number {
  return side.reduce((sum, fighter) => sum + fighter.sila + (fighter.weapon ?? 0) + fighter.chotkost * 0.3, 0);
}

function totalHealth(side: readonly Fighter[]): number {
  return side.reduce((sum, fighter) => sum + fighter.health, 0);
}

const HITS = ['в челюсть', 'по корпусу', 'с ноги', 'в бок', 'по хребту', 'сбоку, не глядя'];

/**
 * Раунды идут по очереди, урон зависит от силы стороны и разброса.
 * Лог пишется человеческим языком: он и есть весь визуал драки.
 */
export function resolveFight(ours: readonly Fighter[], theirs: readonly Fighter[], rng: Rng): FightResult {
  const rounds: FightRound[] = [];
  let ourHealth = totalHealth(ours);
  let theirHealth = totalHealth(theirs);
  const startingHealth = ourHealth;

  const ourPower = sideStrength(ours);
  const theirPower = sideStrength(theirs);
  const ourName = ours.length > 1 ? 'вы' : (ours[0]?.name ?? 'ты');
  const theirName = theirs.length > 1 ? 'они' : (theirs[0]?.name ?? 'он');

  for (let round = 1; round <= MAX_ROUNDS && ourHealth > 0 && theirHealth > 0; round++) {
    const ourHit = Math.max(1, Math.round(ourPower * (0.6 + rng.next() * 0.8)));
    theirHealth -= ourHit;
    rounds.push({ round, text: `${capitalize(ourName)} ${rng.pick(HITS) ?? 'в челюсть'} — минус ${ourHit}.` });

    if (theirHealth <= 0) break;

    const theirHit = Math.max(1, Math.round(theirPower * (0.6 + rng.next() * 0.8)));
    ourHealth -= theirHit;
    rounds.push({ round, text: `${capitalize(theirName)} отвечает ${rng.pick(HITS) ?? 'по корпусу'} — минус ${theirHit}.` });
  }

  const won = theirHealth <= 0 && ourHealth > 0;
  rounds.push({
    round: rounds.length,
    text: won ? 'Разошлись: они на асфальте, ты на ногах.' : 'Отступил, пока цел.',
  });

  return {
    won,
    rounds,
    damageTaken: Math.max(0, Math.min(startingHealth, startingHealth - ourHealth)),
  };
}

function capitalize(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}
